import { useEffect, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import useFetch from "v4/hooks/useFetch";
import { generateUrl } from "v4/services/api.service";
import { PROSPECT_SEARCH, STATISTIC_PROSPECT_LISTING } from "v4/data/apiRoutes";
import { useSearchContext } from "v4/contexts/SearchContext/SearchContext";

export default function useProspectsListingStats(isLoading) {
    const [statsForIds, setStatsForIds] = useState(null);

    const { formMethods, tab } = useSearchContext();
    const { control, getValues } = formMethods ?? {};
    const values = useWatch({ control });
    const isFirstRender = useRef(true);

    const [{ data: allIdsData }, fetchAllIds] = useFetch();
    const [{ data: modulesData, isLoading: areModulesLoading }, fetchStats] = useFetch();
    const modules = modulesData?.modules;
    const hasNoModulesConfigured = !Object.values(modules ?? {}).some(module => module.length);
    const allIds = useMemo(() => allIdsData?.['hydra:member']?.map(({ id }) => id) || [], [allIdsData]);

    useEffect(() => {
        const delay = isFirstRender.current ? 0 : 1000;
        isFirstRender.current = false;

        const timer = setTimeout(() => {
            fetchAllIds({
                url: generateUrl(PROSPECT_SEARCH, {
                    isAllIds: true,
                    ...(tab && { tab: tab.id }),
                }),
                config: {
                    method: 'POST',
                    body: JSON.stringify(getValues()),
                }
            });
        }, delay);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchAllIds, getValues, JSON.stringify(values), tab]);

    useEffect(() => {
        if (!allIds?.length) return;

        setStatsForIds(prev => JSON.stringify(prev) === JSON.stringify(allIds) ? prev : allIds);
    }, [allIds]);

    const hasFetchedModules = useRef(false);
    useEffect(() => {
        if (!statsForIds || hasFetchedModules.current) return;

        hasFetchedModules.current = true;
        fetchStats({ url: generateUrl(STATISTIC_PROSPECT_LISTING) });
    }, [fetchStats, statsForIds]);

    return {
        modules,
        hasNoModulesConfigured,
        areModulesLoading: isLoading || areModulesLoading,
        statsForIds,
    }
}
